/**
 * ELF-CIRCLE hero — "4D Cinematic".
 *
 * A 1:1 port of the `#hero` block from the Claude Design canvas
 * ("ELF-CIRCLE Landing Page Final.dc.html"). Presentation lives in
 * `elf-landing.css`; this file owns the markup and the four pointer-driven
 * behaviours the design script provided:
 *
 *   1. spotlight  — radial wash tracking the cursor across the frame
 *   2. parallax   — background still drifts opposite the cursor
 *   3. magnetic   — CTAs lean toward the cursor
 *   4. reveal     — cursor punches a hole in the mascot to expose the crest
 *
 * Plus the headline word rotator (Culture / Rhythm / Vision / Passion).
 *
 * The hero is deliberately fixed — its art and copy are not editable from the
 * admin UI, so everything here is a bundled import rather than a CMS slot.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ElfHeroDots from './ElfHeroDots';
import ElfHeroNav from './ElfHeroNav';
import useMagnetic from './useMagnetic';
import { useConsultOpener } from './elfConsult';
import crestReveal from '../../../../assets/elf/crest-reveal.png';
import heroStill from '../../../../assets/elf/hero-bg.webp';
import roboHead from '../../../../assets/elf/robo-head-3d.png';
// import { scrollPastHero } from './ElfTear'; // restore with the Discover button

const ROTATOR_WORDS = ['Culture', 'Rhythm', 'Vision', 'Passion'];
const ROTATOR_INTERVAL_MS = 2200;

const TICKER =
  'Entertainment · Lifestyle · Fashion · Artists · Models · Music Creators · Festivals ·';

/**
 * Reveal disc size as a fraction of the mascot box. The design hard-codes
 * `data-r="78"` against a 310px box; expressing it as a ratio keeps the effect
 * proportional as the box scales across breakpoints.
 */
const REVEAL_RATIO = 78 / 310;

/* -------------------------------------------------------------------------- */
/* Word rotator                                                               */
/* -------------------------------------------------------------------------- */

function useWordRotator(words: string[], intervalMs: number) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % words.length),
      intervalMs
    );
    return () => window.clearInterval(id);
  }, [words.length, intervalMs]);

  return index;
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                       */
/* -------------------------------------------------------------------------- */

export const ElfHero: React.FC = () => {
  const frameRef = useRef<HTMLDivElement>(null);
  const paraxRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const faceRef = useRef<HTMLImageElement>(null);
  const botRef = useRef<HTMLImageElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  const magnetic = useMagnetic();
  const getAccess = useConsultOpener({ source: 'hero.access', title: 'Get access' });
  const wordIndex = useWordRotator(ROTATOR_WORDS, ROTATOR_INTERVAL_MS);

  /* --- spotlight + background parallax ------------------------------------ */

  const onFrameMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const frame = frameRef.current;
    if (!frame) return;
    const r = frame.getBoundingClientRect();

    const relX = (e.clientX - r.left) / r.width;
    const relY = (e.clientY - r.top) / r.height;

    frame.style.setProperty('--mx', `${relX * 100}%`);
    frame.style.setProperty('--my', `${relY * 100}%`);

    if (paraxRef.current) {
      paraxRef.current.style.transform = `translate(${-(relX - 0.5) * 26}px, ${
        -(relY - 0.5) * 18
      }px)`;
    }
  }, []);

  const onFrameLeave = useCallback(() => {
    if (paraxRef.current) paraxRef.current.style.transform = '';
  }, []);

  /* --- mascot cursor reveal ------------------------------------------------ */

  const onRevealMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const box = revealRef.current;
    const face = faceRef.current;
    const bot = botRef.current;
    const ring = ringRef.current;
    if (!box || !face || !bot || !ring) return;

    const r = box.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const radius = r.width * REVEAL_RATIO;

    // The crest is clipped to a disc under the cursor…
    face.style.transition = 'clip-path .05s linear';
    face.style.clipPath = `circle(${radius}px at ${x}px ${y}px)`;

    // …while the mascot above it gets a matching hole punched through.
    const hole = `radial-gradient(circle ${radius}px at ${x}px ${y}px, transparent 62%, #000 100%)`;
    bot.style.webkitMaskImage = hole;
    bot.style.maskImage = hole;

    ring.style.left = `${x - radius}px`;
    ring.style.top = `${y - radius}px`;
    ring.style.width = `${radius * 2}px`;
    ring.style.height = `${radius * 2}px`;
    ring.style.opacity = '.8';
  }, []);

  const onRevealLeave = useCallback(() => {
    const face = faceRef.current;
    const bot = botRef.current;
    const ring = ringRef.current;
    if (!face || !bot || !ring) return;

    face.style.transition = 'clip-path .35s ease';
    face.style.clipPath = 'circle(0px at -999px -999px)';
    bot.style.webkitMaskImage = 'none';
    bot.style.maskImage = 'none';
    ring.style.opacity = '0';
  }, []);

  return (
    <div
      id="hero"
      ref={frameRef}
      className="spot elf-hero"
      onPointerMove={onFrameMove}
      onPointerLeave={onFrameLeave}
    >
      {/* film still — ken-burns inside a parallax shell */}
      <div ref={paraxRef} className="elf-hero__parax">
        <div className="elf-hero__kb">
          <img className="elf-hero__still" src={heroStill} alt="" aria-hidden="true" />
        </div>
      </div>
      <div className="elf-hero__scrim" />
      {/* drifting dots — above the still, below the copy, clear of the mascot */}
      <ElfHeroDots avoidRef={revealRef} />

      <ElfHeroNav />

      {/* vertical ticker */}
      <div className="elf-hero__vticker" aria-hidden="true">
        <div>{TICKER}</div>
      </div>

      {/* headline block */}
      <div className="elf-hero__body">
        <span className="anP dwrap" style={{ display: 'block', animationDelay: '.28s' }}>
          <div
            ref={revealRef}
            className="reveal"
            onPointerMove={onRevealMove}
            onPointerLeave={onRevealLeave}
          >
            <img
              ref={faceRef}
              className="rv-face"
              src={crestReveal}
              alt=""
              draggable={false}
              style={{
                WebkitMaskImage: `url(${roboHead})`,
                maskImage: `url(${roboHead})`,
              }}
            />
            <img
              ref={botRef}
              className="rv-bot"
              src={roboHead}
              alt="ELF-CIRCLE mascot"
              draggable={false}
            />
            <div ref={ringRef} className="rv-ring" />
          </div>
        </span>

        <div>
          <span className="mask elf-hero__title">
            <span style={{ animationDelay: '.35s' }}>
              Where{' '}
              <span className="rotbox">
                <b key={wordIndex}>{ROTATOR_WORDS[wordIndex]}</b>
              </span>
            </span>
          </span>
          <span className="mask elf-hero__title">
            <span style={{ animationDelay: '.5s' }}>Lives &amp; Creativity</span>
          </span>
          <span className="mask elf-hero__title">
            <span style={{ animationDelay: '.62s' }}>Thrives</span>
          </span>
        </div>

        <div className="elf-hero__row">
          <p className="an elf-hero__lede" style={{ animationDelay: '.75s' }}>
            The service hub for the ELF world — marketing, web development, events, merch and
            consultations. Built for artists, models and music creators.
          </p>
          <div className="an elf-hero__actions" style={{ animationDelay: '.85s' }}>
            <div className="gborder elf-hero__signup">
              <input className="fld" placeholder="Email or mobile" aria-label="Email or mobile" />
              <button type="button" className="cta mag" {...magnetic} onClick={getAccess}>
                Get Access
              </button>
            </div>
            {/* Discover button — hidden for now. Scrolled to the services rail.
            <button
              type="button"
              className="discover"
              onClick={scrollPastHero
              }
            >
              Discover <i>↗</i>
            </button>
            */}
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <div className="elf-hero__cue" aria-hidden="true">
        <span />
      </div>
    </div>
  );
};

export default ElfHero;
